import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { createEvent, updateEvent, getEvent, getCategories, uploadImage } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { CalendarIcon, Plus, Trash2, ArrowLeft, Save, Youtube, Image, Upload, X, Music, Calendar as CalendarIconAlt, Phone, AlertTriangle } from 'lucide-react';
import { cn } from '../lib/utils';

const EventForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = !!id;
  const fileInputRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [categoriesData, setCategoriesData] = useState({ event_types: [], categories: [], music_subcategories: [] });
  const [imagePreview, setImagePreview] = useState(null);
  const [eventType, setEventType] = useState(''); // 'musical' or 'other'
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    location: '',
    date: null,
    end_date: null,
    category: '',
    subcategory: '',
    image_url: '',
    youtube_url: '',
    is_free: false,
    is_adult_only: false,
    contact_phone: '',
    ticket_types: [
      { type_id: '1', name: 'General', price: 0, quantity: 100, description: '' }
    ]
  });

  useEffect(() => {
    loadCategories();
    if (isEditing) {
      loadEvent();
    }
  }, [id]);

  const loadCategories = async () => {
    try {
      const res = await getCategories();
      setCategoriesData(res.data);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const loadEvent = async () => {
    try {
      const res = await getEvent(id);
      const event = res.data;
      
      // Determine event type based on category/subcategory
      const isMusical = event.subcategory || ['bachata', 'cumbia', 'reggaeton', 'regional', 'tropical', 'rock', 'vallenato', 'pop', 'electronica', 'hip-hop', 'salsa', 'merengue'].includes(event.category);
      setEventType(isMusical ? 'musical' : 'other');
      
      setFormData({
        title: event.title,
        description: event.description,
        location: event.location,
        date: new Date(event.date),
        end_date: event.end_date ? new Date(event.end_date) : null,
        category: event.category,
        subcategory: event.subcategory || '',
        image_url: event.image_url || '',
        youtube_url: event.youtube_url || '',
        is_free: event.is_free,
        is_adult_only: event.is_adult_only || false,
        contact_phone: event.contact_phone || '',
        ticket_types: event.ticket_types || []
      });
      if (event.image_url) {
        setImagePreview(event.image_url);
      }
    } catch (error) {
      console.error('Error loading event:', error);
      toast.error('Error al cargar el evento');
      navigate('/organizer');
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleTicketTypeChange = (index, field, value) => {
    setFormData(prev => {
      const newTypes = [...prev.ticket_types];
      newTypes[index] = { ...newTypes[index], [field]: value };
      return { ...prev, ticket_types: newTypes };
    });
  };

  const addTicketType = () => {
    setFormData(prev => ({
      ...prev,
      ticket_types: [
        ...prev.ticket_types,
        { type_id: String(Date.now()), name: '', price: 0, quantity: 50, description: '' }
      ]
    }));
  };

  const removeTicketType = (index) => {
    if (formData.ticket_types.length === 1) {
      toast.error('Debe haber al menos un tipo de ticket');
      return;
    }
    setFormData(prev => ({
      ...prev,
      ticket_types: prev.ticket_types.filter((_, i) => i !== index)
    }));
  };

  const handleImageSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Por favor selecciona una imagen');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('La imagen debe ser menor a 5MB');
      return;
    }

    setUploadingImage(true);
    try {
      const reader = new FileReader();
      reader.onload = (e) => setImagePreview(e.target.result);
      reader.readAsDataURL(file);

      const res = await uploadImage(file);
      handleChange('image_url', res.data.url);
      toast.success('Imagen subida exitosamente');
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Error al subir la imagen');
      setImagePreview(null);
    } finally {
      setUploadingImage(false);
    }
  };

  const removeImage = () => {
    setImagePreview(null);
    handleChange('image_url', '');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const getYoutubeVideoId = (url) => {
    if (!url) return null;
    const regex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/;
    const match = url.match(regex);
    return match ? match[1] : null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.description || !formData.location || !formData.date) {
      toast.error('Completa todos los campos obligatorios');
      return;
    }

    if (!formData.contact_phone) {
      toast.error('El teléfono de contacto es obligatorio');
      return;
    }

    if (eventType === 'musical' && !formData.subcategory) {
      toast.error('Selecciona un género musical');
      return;
    }

    if (eventType === 'other' && !formData.category) {
      toast.error('Selecciona una categoría');
      return;
    }

    if (formData.ticket_types.some(t => !t.name || t.quantity <= 0)) {
      toast.error('Verifica los tipos de ticket');
      return;
    }

    if (formData.youtube_url && !getYoutubeVideoId(formData.youtube_url)) {
      toast.error('URL de YouTube inválida');
      return;
    }

    setLoading(true);

    try {
      const data = {
        ...formData,
        category: eventType === 'musical' ? 'musical' : formData.category,
        subcategory: eventType === 'musical' ? formData.subcategory : null,
        date: formData.date.toISOString(),
        end_date: formData.end_date?.toISOString() || null,
        ticket_types: formData.ticket_types.map(t => ({
          ...t,
          price: formData.is_free ? 0 : parseFloat(t.price) || 0,
          quantity: parseInt(t.quantity) || 0
        }))
      };

      if (isEditing) {
        await updateEvent(id, data);
        toast.success('Evento actualizado');
      } else {
        await createEvent(data);
        toast.success('Evento creado exitosamente');
      }
      navigate('/organizer');
    } catch (error) {
      console.error('Error saving event:', error);
      toast.error(error.response?.data?.detail || 'Error al guardar el evento');
    } finally {
      setLoading(false);
    }
  };

  const youtubeVideoId = getYoutubeVideoId(formData.youtube_url);

  return (
    <div className="min-h-screen pt-20 pb-12 px-4">
      <div className="max-w-3xl mx-auto">
        <Button
          variant="ghost"
          className="mb-6"
          onClick={() => navigate('/organizer')}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Volver al Dashboard
        </Button>

        <div className="glass-card p-8">
          <h1 className="text-2xl font-bold mb-6">
            {isEditing ? 'Editar Evento' : 'Crear Nuevo Evento'}
          </h1>

          {/* Event Type Selection */}
          {!isEditing && !eventType && (
            <div className="mb-8">
              <h2 className="text-lg font-semibold mb-4">¿Qué tipo de evento deseas crear?</h2>
              <div className="grid grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => setEventType('musical')}
                  className="p-6 rounded-xl border-2 border-border hover:border-primary transition-all text-center group"
                >
                  <Music className="w-12 h-12 mx-auto mb-3 text-primary group-hover:scale-110 transition-transform" />
                  <p className="font-semibold">Evento Musical</p>
                  <p className="text-sm text-muted-foreground mt-1">Conciertos, festivales, fiestas</p>
                </button>
                <button
                  type="button"
                  onClick={() => setEventType('other')}
                  className="p-6 rounded-xl border-2 border-border hover:border-primary transition-all text-center group"
                >
                  <CalendarIconAlt className="w-12 h-12 mx-auto mb-3 text-orange-500 group-hover:scale-110 transition-transform" />
                  <p className="font-semibold">Otro Tipo de Evento</p>
                  <p className="text-sm text-muted-foreground mt-1">Deportes, teatro, conferencias</p>
                </button>
              </div>
            </div>
          )}

          {/* Main Form */}
          {(eventType || isEditing) && (
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Event Type Badge */}
              {eventType && !isEditing && (
                <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                  <div className="flex items-center gap-2">
                    {eventType === 'musical' ? (
                      <Music className="w-5 h-5 text-primary" />
                    ) : (
                      <CalendarIconAlt className="w-5 h-5 text-orange-500" />
                    )}
                    <span className="font-medium">
                      {eventType === 'musical' ? 'Evento Musical' : 'Otro Tipo de Evento'}
                    </span>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setEventType('');
                      handleChange('category', '');
                      handleChange('subcategory', '');
                    }}
                  >
                    Cambiar
                  </Button>
                </div>
              )}

              {/* Basic Info */}
              <div className="space-y-4">
                <div>
                  <Label htmlFor="title">Título del Evento *</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => handleChange('title', e.target.value)}
                    placeholder="Ej: Concierto de Rock en Vivo"
                    required
                    data-testid="event-title-input"
                  />
                </div>

                <div>
                  <Label htmlFor="description">Descripción *</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => handleChange('description', e.target.value)}
                    placeholder="Describe tu evento..."
                    rows={4}
                    required
                    data-testid="event-description-input"
                  />
                </div>

                {/* Category/Subcategory Selection */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {eventType === 'musical' ? (
                    <div>
                      <Label>Género Musical *</Label>
                      <Select
                        value={formData.subcategory}
                        onValueChange={(value) => handleChange('subcategory', value)}
                      >
                        <SelectTrigger data-testid="event-subcategory-select">
                          <SelectValue placeholder="Selecciona un género" />
                        </SelectTrigger>
                        <SelectContent>
                          {categoriesData.music_subcategories?.map((sub) => (
                            <SelectItem key={sub.id} value={sub.id}>
                              {sub.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ) : (
                    <div>
                      <Label>Categoría *</Label>
                      <Select
                        value={formData.category}
                        onValueChange={(value) => handleChange('category', value)}
                      >
                        <SelectTrigger data-testid="event-category-select">
                          <SelectValue placeholder="Selecciona una categoría" />
                        </SelectTrigger>
                        <SelectContent>
                          {categoriesData.categories?.map((cat) => (
                            <SelectItem key={cat.id} value={cat.id}>
                              {cat.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  <div>
                    <Label htmlFor="location">Ubicación *</Label>
                    <Input
                      id="location"
                      value={formData.location}
                      onChange={(e) => handleChange('location', e.target.value)}
                      placeholder="Ej: Estadio Nacional, Tegucigalpa"
                      required
                      data-testid="event-location-input"
                    />
                  </div>
                </div>

                {/* Contact Phone - Required */}
                <div>
                  <Label htmlFor="contact_phone" className="flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    Teléfono de Contacto *
                  </Label>
                  <Input
                    id="contact_phone"
                    type="tel"
                    value={formData.contact_phone}
                    onChange={(e) => handleChange('contact_phone', e.target.value)}
                    placeholder="Ej: +504 9999-9999"
                    required
                    data-testid="event-phone-input"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Este número solo será visible para el administrador del sitio
                  </p>
                </div>

                {/* Date/Time Selection */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Fecha y Hora de Inicio *</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            "w-full justify-start text-left font-normal",
                            !formData.date && "text-muted-foreground"
                          )}
                          data-testid="event-date-trigger"
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {formData.date ? (
                            format(formData.date, "PPP 'a las' h:mm a", { locale: es })
                          ) : (
                            "Selecciona fecha"
                          )}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={formData.date}
                          onSelect={(date) => handleChange('date', date)}
                          disabled={(date) => date < new Date()}
                          initialFocus
                        />
                        <div className="p-3 border-t space-y-2">
                          <Label>Hora</Label>
                          <div className="flex gap-2 items-center">
                            <Input
                              type="number"
                              min="1"
                              max="12"
                              placeholder="12"
                              className="w-16 text-center"
                              value={formData.date ? (formData.date.getHours() % 12 || 12) : ''}
                              onChange={(e) => {
                                let hour = parseInt(e.target.value) || 12;
                                if (hour > 12) hour = 12;
                                if (hour < 1) hour = 1;
                                const newDate = formData.date ? new Date(formData.date) : new Date();
                                const isPM = newDate.getHours() >= 12;
                                const newHour = isPM ? (hour === 12 ? 12 : hour + 12) : (hour === 12 ? 0 : hour);
                                newDate.setHours(newHour);
                                handleChange('date', newDate);
                              }}
                              data-testid="event-hour-input"
                            />
                            <span className="text-lg font-bold">:</span>
                            <Input
                              type="number"
                              min="0"
                              max="59"
                              placeholder="00"
                              className="w-16 text-center"
                              value={formData.date ? String(formData.date.getMinutes()).padStart(2, '0') : ''}
                              onChange={(e) => {
                                let minutes = parseInt(e.target.value) || 0;
                                if (minutes > 59) minutes = 59;
                                if (minutes < 0) minutes = 0;
                                const newDate = formData.date ? new Date(formData.date) : new Date();
                                newDate.setMinutes(minutes);
                                handleChange('date', newDate);
                              }}
                              data-testid="event-minute-input"
                            />
                            <div className="flex rounded-lg overflow-hidden border border-border">
                              <button
                                type="button"
                                className={cn(
                                  "px-3 py-2 text-sm font-medium transition-colors",
                                  formData.date && formData.date.getHours() < 12
                                    ? "bg-primary text-primary-foreground"
                                    : "bg-background hover:bg-muted"
                                )}
                                onClick={() => {
                                  const newDate = formData.date ? new Date(formData.date) : new Date();
                                  const currentHour = newDate.getHours();
                                  if (currentHour >= 12) {
                                    newDate.setHours(currentHour - 12);
                                  }
                                  handleChange('date', newDate);
                                }}
                                data-testid="event-am-button"
                              >
                                AM
                              </button>
                              <button
                                type="button"
                                className={cn(
                                  "px-3 py-2 text-sm font-medium transition-colors border-l border-border",
                                  formData.date && formData.date.getHours() >= 12
                                    ? "bg-primary text-primary-foreground"
                                    : "bg-background hover:bg-muted"
                                )}
                                onClick={() => {
                                  const newDate = formData.date ? new Date(formData.date) : new Date();
                                  const currentHour = newDate.getHours();
                                  if (currentHour < 12) {
                                    newDate.setHours(currentHour + 12);
                                  }
                                  handleChange('date', newDate);
                                }}
                                data-testid="event-pm-button"
                              >
                                PM
                              </button>
                            </div>
                          </div>
                        </div>
                      </PopoverContent>
                    </Popover>
                  </div>

                  <div>
                    <Label>Fecha de Fin (Opcional)</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            "w-full justify-start text-left font-normal",
                            !formData.end_date && "text-muted-foreground"
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {formData.end_date ? (
                            format(formData.end_date, "PPP", { locale: es })
                          ) : (
                            "Selecciona fecha de fin"
                          )}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={formData.end_date}
                          onSelect={(date) => handleChange('end_date', date)}
                          disabled={(date) => date < formData.date}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>

                {/* Media Section */}
                <div className="space-y-4 p-4 rounded-xl border border-border bg-muted/30">
                  <h3 className="font-semibold flex items-center gap-2">
                    <Image className="w-5 h-5" />
                    Multimedia
                  </h3>
                  
                  {/* Image Upload */}
                  <div>
                    <Label>Imagen del Evento</Label>
                    <p className="text-xs text-muted-foreground mb-2">
                      Dimensiones recomendadas: 1920x1080px (16:9). Máximo 5MB. Formatos: JPG, PNG, WEBP
                    </p>
                    
                    {imagePreview ? (
                      <div className="relative rounded-xl overflow-hidden">
                        <img 
                          src={imagePreview} 
                          alt="Preview" 
                          className="w-full h-48 object-cover"
                        />
                        <Button
                          type="button"
                          variant="destructive"
                          size="icon"
                          className="absolute top-2 right-2"
                          onClick={removeImage}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                        {uploadingImage && (
                          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                            <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          </div>
                        )}
                      </div>
                    ) : (
                      <div 
                        className="border-2 border-dashed border-border rounded-xl p-8 text-center cursor-pointer hover:border-primary/50 transition-colors"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <Upload className="w-10 h-10 mx-auto text-muted-foreground mb-2" />
                        <p className="text-sm text-muted-foreground">
                          Haz clic para subir o arrastra una imagen
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          1920x1080px recomendado
                        </p>
                      </div>
                    )}
                    
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleImageSelect}
                      data-testid="event-image-upload"
                    />
                    
                    <div className="mt-3">
                      <Label htmlFor="image_url" className="text-xs text-muted-foreground">
                        O ingresa una URL de imagen:
                      </Label>
                      <Input
                        id="image_url"
                        value={formData.image_url}
                        onChange={(e) => {
                          handleChange('image_url', e.target.value);
                          setImagePreview(e.target.value);
                        }}
                        placeholder="https://ejemplo.com/imagen.jpg"
                        className="mt-1"
                        data-testid="event-image-input"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="youtube_url" className="flex items-center gap-2">
                      <Youtube className="w-4 h-4 text-red-500" />
                      URL de Video de YouTube
                    </Label>
                    <Input
                      id="youtube_url"
                      value={formData.youtube_url}
                      onChange={(e) => handleChange('youtube_url', e.target.value)}
                      placeholder="https://www.youtube.com/watch?v=..."
                      data-testid="event-youtube-input"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Agrega un video promocional o trailer de tu evento
                    </p>
                  </div>

                  {youtubeVideoId && (
                    <div className="mt-4">
                      <Label>Vista previa del video</Label>
                      <div className="aspect-video rounded-lg overflow-hidden mt-2 bg-black">
                        <iframe
                          width="100%"
                          height="100%"
                          src={`https://www.youtube.com/embed/${youtubeVideoId}`}
                          title="YouTube video preview"
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Event Options */}
              <div className="space-y-4">
                {/* Free Event Toggle */}
                <div className="flex items-center justify-between p-4 rounded-xl border border-border">
                  <div>
                    <Label htmlFor="is_free" className="font-medium">Evento Gratuito</Label>
                    <p className="text-sm text-muted-foreground">
                      Los tickets serán gratuitos para los asistentes
                    </p>
                  </div>
                  <Switch
                    id="is_free"
                    checked={formData.is_free}
                    onCheckedChange={(checked) => handleChange('is_free', checked)}
                    data-testid="event-free-switch"
                  />
                </div>

                {/* Adult Only Toggle */}
                <div className="flex items-center justify-between p-4 rounded-xl border border-orange-500/30 bg-orange-500/5">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-orange-500 mt-0.5" />
                    <div>
                      <Label htmlFor="is_adult_only" className="font-medium">Solo Mayores de Edad (18+)</Label>
                      <p className="text-sm text-muted-foreground">
                        Se mostrará una advertencia a los compradores antes de la compra
                      </p>
                    </div>
                  </div>
                  <Switch
                    id="is_adult_only"
                    checked={formData.is_adult_only}
                    onCheckedChange={(checked) => handleChange('is_adult_only', checked)}
                    data-testid="event-adult-switch"
                  />
                </div>
              </div>

              {/* Ticket Types */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <Label className="text-lg font-semibold">Tipos de Tickets</Label>
                  <Button type="button" variant="outline" size="sm" onClick={addTicketType}>
                    <Plus className="w-4 h-4 mr-2" />
                    Agregar Tipo
                  </Button>
                </div>

                <div className="space-y-4">
                  {formData.ticket_types.map((ticket, index) => (
                    <div
                      key={ticket.type_id}
                      className="p-4 rounded-xl border border-border space-y-4"
                      data-testid={`ticket-type-${index}`}
                    >
                      <div className="flex justify-between items-center">
                        <span className="font-medium">Tipo {index + 1}</span>
                        {formData.ticket_types.length > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => removeTicketType(index)}
                          >
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        )}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <Label>Nombre *</Label>
                          <Input
                            value={ticket.name}
                            onChange={(e) => handleTicketTypeChange(index, 'name', e.target.value)}
                            placeholder="Ej: General, VIP, Palco"
                            required
                          />
                        </div>
                        <div>
                          <Label>Precio (L.)</Label>
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={formData.is_free ? 0 : ticket.price}
                            onChange={(e) => handleTicketTypeChange(index, 'price', e.target.value)}
                            disabled={formData.is_free}
                          />
                        </div>
                        <div>
                          <Label>Cantidad *</Label>
                          <Input
                            type="number"
                            min="1"
                            value={ticket.quantity}
                            onChange={(e) => handleTicketTypeChange(index, 'quantity', e.target.value)}
                            required
                          />
                        </div>
                      </div>

                      <div>
                        <Label>Descripción (Opcional)</Label>
                        <Input
                          value={ticket.description || ''}
                          onChange={(e) => handleTicketTypeChange(index, 'description', e.target.value)}
                          placeholder="Ej: Incluye acceso a backstage"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Submit */}
              <div className="flex gap-4 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/organizer')}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  className="btn-primary flex-1"
                  disabled={loading || uploadingImage}
                  data-testid="save-event-button"
                >
                  {loading ? (
                    'Guardando...'
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      {isEditing ? 'Actualizar Evento' : 'Crear Evento'}
                    </>
                  )}
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default EventForm;
